/**
 * SAP CONFIDENTIAL
 *
 * This software is confidential proprietary software licensed by SAP SE
 * and is not to be used, duplicated or disclosed to anyone without the
 * prior written permission of SAP.
 *
 * (C) 2020 Copyright SAP SE.
 */
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ModalModule as UserModel } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { AgGridModule } from 'ag-grid-angular';
import { InterceptService } from './services/serverurl.interceptor';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { UiSwitchModule } from 'ngx-ui-switch';
import { NgChartsModule } from 'ng2-charts';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CustomTranslateLoader } from './custom-translate-loader.component';

//Tabs
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { PopoverModule } from 'ngx-bootstrap/popover';

//primeNG
import { OrganizationChartModule } from 'primeng/organizationchart';
import { PanelModule } from 'primeng/panel';
import { TreeModule } from 'primeng/tree';
import { SliderModule } from 'primeng/slider';
import { ListboxModule } from 'primeng/listbox';
import { CalendarModule } from 'primeng/calendar';
import { ChipsModule } from 'primeng/chips';

//Directives
import { LoadButtonDirective } from '../shared/directives/load-button/load-button.directive';

//Components
import { DistributorSearchComponent } from './distributor-search/distributor-search.component';
import { WarnignMessageComponent } from './warning-message.component';
import { FiltersComponent } from './filters/filters.component';
import { VolumeTransferComponent } from './volume-transfer/volume-transfer.component';
import { VolumeTransferConfirmationModalComponent } from './volume-transfer/volume-transfer-confirmation-modal.component';
import { NavigateWizardComponent } from './navigate-wizard/navigate-wizard.component';
import { ChangeSponsorComponent } from './changesponsor/change-sponsor.component';
import { ChangeEnrollerComponent } from './changeenroller/change-enroller.component';
import { ChangeSponsorConfimationModalComponent } from './changesponsor/change-sponsor-confimation-modal.component';
import { BookmarksComponent } from './bookmarks/bookmarks.component';
import { DeleteConfirmationModal } from './delete-confirmation-modal/delete-confirmation-modal.component';
import { PageHelpComponent } from './page-help/page-help.component';
import { RequirementsModalComponent } from './requirements-modal/requirements-modal.component';
import { ChangeOrganizationConfimationModalComponent } from './change-organization/change-organization-confirmation-modal.component';
import { ChangeParentOrganizationConfirmationModalComponent } from './change-parent-organization/change-parent-organization-confirmation-modal.component';
import { ChangeOrgDownlineConfirmationModalComponent } from './change-organization-downline/change-org-downline-confirmation-modal.component';

// pipes
import {
  CountryBaseCodeFilterPipe,
  AuthorFilterPipe,
  ReportDateFilterPipe,
  TagFilterPipe
} from './pipes/CountryBaseCodeFilterPipe';
import { OrderByPipe } from './pipes/orderByPipe';
import { SortByPipe } from './pipes/sorting';
import {
  DateFilterPipe,
  DateTFilterPipe,
  DateTimePipe,
  DateFormatPipe,
  DateMonthFilterPipe,
  DateTimeFilterPipe,
  DatePeriodFilterPipe,
  DatefilterCustomPipe,
  DateTimeTimeZoneFilterPipe
} from './pipes/DatePipe';
import { CapitalizePipe } from './pipes/CapitalizeFilterPipe';
import { HomeSearchFilterPipe, HomeSearchFilterPipeCount } from './pipes/HomeSearchFilterPipe';
import { CardFilterPipe, CardTabFilterPipe } from './pipes/CardFilterPipe';
import {
  VolumeBaseCodeFilterPipe,
  VolumeSubTypeCodeFilterPipe,
  VolumeMetricTypeCodeFilterPipe,
  VolumeTypeCodeFilterPipe,
  TitleByLeastRankFilterPipe,
  VolumeSubTypeCodeFilterVWPipe
} from './pipes/VolumeBaseCodeFilterPipe';
import {
  FilterByFieldPipe,
  FilterOutByFieldPipe,
  FilterPipe,
  FilterByArrayPipe,
  FilterOutByUserGroupPipe,
  FilterOutByProgramCode
} from './pipes/FilterByFieldPipe';
import { PlanFilterPipe, RegionFilterPipe } from './pipes/BonusFilterPipe';
import { ColFilterPipe, ColShowFilterPipe } from './pipes/ColumnSelectorPipe';
import { FilterSearchSelectorPipe, FilterSearchSelectorShowPipe } from './pipes/FilterSearchSelectorPipe';
import { GroupByPropertyPipe } from './pipes/groupByPropertyPipe';
import {
  FilterOutSelectedRegionPipe,
  FilterSelectedRegionPipe,
  FilterOutSelectedRegionByCodePipe
} from './pipes/filterRegionPipe';
import { CurrencySymbolPipe } from './pipes/CurrencySymbolPipe';

// services
import { DistributorSupportService } from './services/distributorSupport.service';
import { UsersService } from './services/users.service';
import { CompanyService } from './services/company.service';
import { GlobalSettingsService } from './services/globalSettings.service';
import { CompanyGenealogyConfigService } from './services/companyGenealogyConfig.service';
import { userDataService } from './services/userData.service';
import { MemberService } from './services/member.service';
import { TaxFeeSetupService } from './services/tax-fee-setup.service';

//components
import { PermissionsCustomHeader } from '../components/administration/users/permissions-custom-header/permissions-custom-header.component';
import { PermissionsAccessStepRender } from '../components/administration/users/permissions-access-stepper/permissions-access-stepper.component';
import { PermissionsLevelAccessSliderRender } from '../components/administration/users/permissions-level-access-slider/permissions-level-access-slider.component';
import { BookmarkPopoverComponent } from './bookmark-popover/bookmark-popover.component';
import { RecentlyViewedComponent } from './recently-viewed/recently-viewed.component';
import { UtilitiesPanelComponent } from './utilities-panel/utilities-panel.component';
import { MoveOrderComponent } from './move-order/move-order.component';
import { MoveOrderConfimationModalComponent } from './move-order/move-order-confirmation-modal.component';
import { MoveOrderService } from './move-order/move-order.service';
import { ClientIDRendererComponent } from './clientid-renderer/clientid-renderer.component';
import { JsonRendererComponent } from './json-renderer/json-renderer.component';
import { ReportTagsComponent } from './report-tags/report-tags.component';
import { ActionAlertModalComponent } from './action-alert-modal/action-alert-modal.component';
import { VolumeRequirementsModalComponent } from './volume-requirements-modal/volume-requirements-modal.component';
import { CommonFlagsModalComponent } from './common-flags-modal/common-flags-modal.component';
import { AlertModalComponent } from './alert-modal/alert-modal.component';
import { ChangeOrganizationComponent } from './change-organization/change-organization.component';
import { OrganizationSearchComponent } from './organization-search/organization-search.component';
import { ChangeParentOrganizationComponent } from './change-parent-organization/change-parent-organization.component';
import { ChangeOrganizationDownlineComponent } from './change-organization-downline/change-organization-downline.component';
import { TranslateVorwerkPipe } from './pipes/translate-vorwerk.pipe';
import { TwoDecimalRestrictDirective } from './directives/two-decimal-restrict/two-decimal-restrict.directive';
import { SortablejsModule } from 'nxt-sortablejs';
import { CriteriaRateModalComponent } from 'app/components/application/planbuilder/compRateCard/criteria-rate-modal/criteria-rate-modal.component';
import { MetricModalComponent } from './metric-modal/metric-modal.component';
import { MetricLinkRendererComponent } from './metric-link-renderer/metric-link-renderer.component';
import { AboutModalComponent } from './about-modal/about-modal.component';

export function bsDatepickerGlobalConfigFactory(): BsDatepickerConfig {
  const config = new BsDatepickerConfig();
  config.displayMonths = 1;      
  config.showWeekNumbers = false; 
  config.adaptivePosition = true;
  return config;
}

@NgModule({
  declarations: [
    DistributorSearchComponent,
    WarnignMessageComponent,
    LoadButtonDirective,
    FiltersComponent,
    VolumeTransferComponent,
    VolumeTransferConfirmationModalComponent,
    CountryBaseCodeFilterPipe,
    OrderByPipe,
    TranslateVorwerkPipe,
    SortByPipe,
    DateFilterPipe,
    DateTFilterPipe,
    DateTimePipe,
    DateFormatPipe,
    DateMonthFilterPipe,
    DateTimeFilterPipe,
    DateTimeTimeZoneFilterPipe,
    DatePeriodFilterPipe,
    CapitalizePipe,
    CardFilterPipe,
    CardTabFilterPipe,
    NavigateWizardComponent,
    VolumeBaseCodeFilterPipe,
    VolumeSubTypeCodeFilterPipe,
    VolumeMetricTypeCodeFilterPipe,
    VolumeSubTypeCodeFilterVWPipe,
    VolumeTypeCodeFilterPipe,
    TitleByLeastRankFilterPipe,
    ChangeSponsorComponent,
    ChangeEnrollerComponent,
    ChangeSponsorConfimationModalComponent,
    ChangeOrganizationConfimationModalComponent,
    ChangeParentOrganizationConfirmationModalComponent,
    ChangeOrgDownlineConfirmationModalComponent,
    HomeSearchFilterPipe,
    HomeSearchFilterPipeCount,
    PermissionsCustomHeader,
    PermissionsAccessStepRender,
    PermissionsLevelAccessSliderRender,
    BookmarksComponent,
    BookmarkPopoverComponent,
    RecentlyViewedComponent,
    UtilitiesPanelComponent,
    FilterByFieldPipe,
    FilterOutByFieldPipe,
    FilterPipe,
    FilterByArrayPipe,
    DeleteConfirmationModal,
    MoveOrderComponent,
    MoveOrderConfimationModalComponent,
    ClientIDRendererComponent,
    JsonRendererComponent,
    PlanFilterPipe,
    RegionFilterPipe,
    AuthorFilterPipe,
    ReportDateFilterPipe,
    DatefilterCustomPipe,
    TagFilterPipe,
    ReportTagsComponent,
    ColFilterPipe,
    ColShowFilterPipe,
    FilterSearchSelectorPipe,
    FilterSearchSelectorShowPipe,
    ActionAlertModalComponent,
    GroupByPropertyPipe,
    FilterOutSelectedRegionPipe,
    FilterSelectedRegionPipe,
    FilterOutSelectedRegionByCodePipe,
    VolumeRequirementsModalComponent,
    PageHelpComponent,
    RequirementsModalComponent,
    FilterOutByUserGroupPipe,
    FilterOutByProgramCode,
    CommonFlagsModalComponent,
    CurrencySymbolPipe,
    AlertModalComponent,
    ChangeOrganizationComponent,
    OrganizationSearchComponent,
    ChangeParentOrganizationComponent,
    ChangeParentOrganizationConfirmationModalComponent,
    ChangeOrganizationDownlineComponent,
    ChangeOrgDownlineConfirmationModalComponent,
    TwoDecimalRestrictDirective,
    CriteriaRateModalComponent,
    MetricModalComponent,
    MetricLinkRendererComponent,
    AboutModalComponent
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    PopoverModule.forRoot(),
    ButtonsModule,
    NgbModule,
    UserModel.forRoot(),
    CommonModule,
    BsDropdownModule.forRoot(),
    BsDatepickerModule.forRoot(),
    AlertModule.forRoot(),
    PaginationModule.forRoot(),
    TypeaheadModule.forRoot(),
    NgMultiSelectDropDownModule.forRoot(),
    // MultiselectDropdownModule,
    OrganizationChartModule,
    PanelModule,
    TreeModule,
    // Ng2TableModule,
    SliderModule,
    UiSwitchModule,
    ListboxModule,
    AgGridModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient]
      }
    }),
    HttpClientModule,
    NgChartsModule,
    ChipsModule,
    CalendarModule,
    SortablejsModule
    // FundamentalNgxModule
  ],
  exports: [
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    PopoverModule,
    ButtonsModule,
    NgbModule,
    UserModel,
    AlertModule,
    UiSwitchModule,
    TranslateModule,
    BsDropdownModule,
    BsDatepickerModule,
    PaginationModule,
    TypeaheadModule,
    NgMultiSelectDropDownModule,
    // MultiselectDropdownModule,
    OrganizationChartModule,
    PanelModule,
    TreeModule,
    // Ng2TableModule,
    AgGridModule,
    NgChartsModule,
    // HttpInterceptorModule,
    DistributorSearchComponent,
    OrganizationSearchComponent,
    WarnignMessageComponent,
    LoadButtonDirective,
    CountryBaseCodeFilterPipe,
    OrderByPipe,
    SortByPipe,
    DateFilterPipe,
    DateTFilterPipe,
    DateTimePipe,
    DateFormatPipe,
    DateMonthFilterPipe,
    DateTimeFilterPipe,
    DateTimeTimeZoneFilterPipe,
    DatePeriodFilterPipe,
    CapitalizePipe,
    CardFilterPipe,
    CardTabFilterPipe,
    FiltersComponent,
    VolumeTransferComponent,
    NavigateWizardComponent,
    VolumeBaseCodeFilterPipe,
    VolumeSubTypeCodeFilterPipe,
    VolumeMetricTypeCodeFilterPipe,
    VolumeSubTypeCodeFilterVWPipe,
    VolumeTypeCodeFilterPipe,
    TitleByLeastRankFilterPipe,
    ChangeSponsorComponent,
    ChangeEnrollerComponent,
    HomeSearchFilterPipe,
    HomeSearchFilterPipeCount,
    PermissionsCustomHeader,
    SliderModule,
    ListboxModule,
    CurrencySymbolPipe,
    BookmarksComponent,
    BookmarkPopoverComponent,
    RecentlyViewedComponent,
    UtilitiesPanelComponent,
    FilterByFieldPipe,
    FilterOutByFieldPipe,
    FilterPipe,
    FilterByArrayPipe,
    MoveOrderComponent,
    ClientIDRendererComponent,
    PlanFilterPipe,
    RegionFilterPipe,
    AuthorFilterPipe,
    ReportDateFilterPipe,
    TagFilterPipe,
    ReportTagsComponent,
    ColFilterPipe,
    ColShowFilterPipe,
    FilterSearchSelectorPipe,
    FilterSearchSelectorShowPipe,
    GroupByPropertyPipe,
    FilterOutSelectedRegionPipe,
    FilterSelectedRegionPipe,
    FilterOutSelectedRegionByCodePipe,
    DatefilterCustomPipe,
    // FundamentalNgxModule,
    PageHelpComponent,
    FilterOutByUserGroupPipe,
    FilterOutByProgramCode,
    ChangeOrganizationComponent,
    ChangeParentOrganizationComponent,
    ChangeOrganizationDownlineComponent,
    TranslateVorwerkPipe,
    TwoDecimalRestrictDirective,
    ChipsModule,
    CalendarModule,
    SortablejsModule
  ],
  providers: [
    TaxFeeSetupService,
    // ResignationService,
    DistributorSupportService,
    MemberService,
    UsersService,
    CompanyService,
    MoveOrderService,
    CompanyGenealogyConfigService,
    userDataService,
    InterceptService,
    GlobalSettingsService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptService,
      multi: true
    },
    {
      provide: BsDatepickerConfig,
      useFactory: bsDatepickerGlobalConfigFactory
    }
  ]
})
export class SharedModule {}
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './i18n/', '.json');
}
